import React from 'react';
import { styled, Colors } from 'themes';
import { IoIosBriefcase, IoMdCheckmark, IoIosClose } from 'react-icons/io';
import Avatar from './avatar';
import NameAgeDisplay from './name-age-display';
import StarRating from './star-rating';
import Ratings from './rating';
import durationUtil from 'utils/duration';
import { useTranslation } from 'react-i18next';
import {
  Payload,
  TaskCompletedPayload,
  TaskBookedPayload,
  YepSearchStartedPayload,
} from './types';
import { TaskScheduleType } from 'utils/clientConstants';
import { capitalize } from 'utils/string';
import { createDisplayDateTimeAndPerform } from 'utils/taskUtils';
import Button from 'components/buttons';
import { ROUTES } from 'navigation/route-map';

const ICON_MAP = {
  IoIosBriefcase: IoIosBriefcase,
  IoMdCheckmark: IoMdCheckmark,
};

export enum Type {
  TASK_BOOKED = 'task-booked',
  TASK_COMPLETED = 'task-completed',
  YEP_SEARCH_STARTED = 'yep-search-started',
  YEP_FIRST_TASK = 'yep-first-task',
  YEP_REACHED_LEVEL = 'yep-reached-level',
  // YEP_OF_THE_MONTH = 'yep-of-the-month',
  SHARE = 'share',
  STATISTIC = 'statistic',
}

interface ComponentProps<T> {
  onClose: () => void;
  payload: T;
  translate: Translate;
  typedTranslate: Translate;
}

export const createTypedTranslate = (
  type: Type,
  payload: Payload,
  translate: Translate
): Translate => {
  let base = `component--public-activity-item.${type}`;
  if (payload.isCompany) {
    base = `${base}-isCompany`;
  }

  return (modifier, args) => translate(`${base}.${modifier}`, args);
};

interface LabeledItemsProps {
  items: { title: string; text: string }[];
  // DividerComponent?: React.ComponentType;
}

const LabeledItems = (props: LabeledItemsProps) => {
  const { items } = props;

  return (
    <Wrapper>
      {items.map(({ title, text }, i) => {
        return (
          <React.Fragment key={i}>
            <LabelTitle>{title}</LabelTitle>
            <LabelContent>{text}</LabelContent>
          </React.Fragment>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 1rem 1.5rem;
`;

const LabelTitle = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.battleshipGrey};
`;

const LabelContent = styled.p`
  font-size: 1.5rem;
  margin-bottom: 5px;
`;

const YepstrPlusInfo = (props: { isCompany: boolean }) => {
  const { isCompany, typedTranslate } = props;

  const items = isCompany
    ? [
        typedTranslate('yepstr-plus-info-isCompany-1'),
        typedTranslate('yepstr-plus-info-isCompany-2'),
        typedTranslate('yepstr-plus-info-isCompany-3'),
      ]
    : [
        typedTranslate('yepstr-plus-info-1'),
        typedTranslate('yepstr-plus-info-2'),
      ];

  return (
    <>
      {items.map((text, i) => {
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
            <IoMdCheckmark size={18} color={Colors.greenish} />
            <YepstrPlusItem>{text}</YepstrPlusItem>
          </div>
        );
      })}
    </>
  );
};

const YepstrPlusItem = styled.p`
  font-size: 1.5rem;
  margin-left: 3px;
`;

const GenericWrapperComponent = props => {
  const { onClose, image, header, children } = props;
  return (
    <GenericWrapperContainer>
      <HeaderContainer image={image}>{header}</HeaderContainer>
      <Content>{children}</Content>
      <CloseButton as="a" onClick={onClose}>
        <IoIosClose size={32} color="white" />
      </CloseButton>
    </GenericWrapperContainer>
  );
};

const CloseButton = styled.a`
  position: absolute;
  top: 5px;
  right: 5px;
`;

const GenericWrapperContainer = styled.article`
  position: relative;
  box-shadow: 0 2rem 5rem rgba(0, 0, 0, 0.2);
`;

const HeaderContainer = styled.div`
  position: relative;
  max-width: 375px;
  min-width: 300px;
  height: 140px;

  background-image: linear-gradient(241deg, rgba(43, 44, 61, 0.2), #2b2c3d),
    ${({ image }: { image: string }) => `url(${image})`};
  background-size: cover;
  border-radius: 3px;
  overflow: hidden;
  width: 100%;
`;

const Content = styled.div`
  background-color: white;
`;

const GenericTaskLikeComponent = props => {
  const {
    subtitle,
    title,
    age,
    rating,
    avatar,
    name,
    smallerIconName,
    children,
  } = props;
  const IconComponent = ICON_MAP[smallerIconName];
  return (
    <GenericWrapperComponent
      {...props}
      header={
        <Header>
          {Boolean(subtitle) && (
            <HeaderText>
              {Boolean(smallerIconName) && (
                <>
                  <IconComponent />
                  {'  '}
                </>
              )}
              {subtitle}
            </HeaderText>
          )}
          {Boolean(title) && <CategoryName>{title}</CategoryName>}
          {Boolean((name && age) || rating || avatar) && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {Boolean(avatar) && <Avatar src={avatar} size={[26]} mr={[2]} />}
              <div>
                {Boolean(name && age) && (
                  <NameAgeDisplay age={age!} name={name!} />
                )}
                {Boolean(rating) && <StarRating rating={rating!} size={[10]} />}
              </div>
            </div>
          )}
        </Header>
      }
    >
      {children}
    </GenericWrapperComponent>
  );
};

const HeaderText = styled.p`
  font-size: 1.2rem;
  color: white;
  margin-bottom: 0;
`;

const CategoryName = styled.h4`
  font-size: 2rem;
  color: white;
  margin-bottom: 5px;
`;

const YepSearchStartedComponent = (
  props: ComponentProps<YepSearchStartedPayload>
) => {
  const {
    payload,
    payload: {
      image,
      isCompany,
      subcategories,
      scheduledAt,
      duration,
      scheduleType,
    },
    translate,
    typedTranslate,
  } = props;

  const items = [];
  if (subcategories.length > 0) {
    items.push({
      title: translate('subcategories-title'),
      text: subcategories.join(', '),
    });
  }
  if (scheduleType === TaskScheduleType.SPECIFIC) {
    items.push({
      title: translate('scheduledAt-title'),
      text: capitalize(
        createDisplayDateTimeAndPerform({ scheduledAt, duration })
      ),
    });
  }

  return (
    <GenericTaskLikeComponent
      {...props}
      image={image}
      subtitleIconName={isCompany ? 'IoIosBriefcase' : ''}
      subtitle={typedTranslate('subtitle', payload)}
      title={typedTranslate('title', payload)}
    >
      {items.length > 0 && (
        <>
          <div>
            <LabeledItems items={items} />
            <YepstrPlus>
              {!isCompany && (
                <YepstrPlusText>
                  Alltid med Yepstrs trygghetspaket
                </YepstrPlusText>
              )}
              <YepstrPlusInfo
                typedTranslate={typedTranslate}
                isCompany={isCompany}
              />
            </YepstrPlus>
            <CtaSection>
              <Button
                label={translate('cta-buttonlabel')}
                to={ROUTES.DOWNLOAD}
                fullWidth
              />
            </CtaSection>
          </div>
        </>
      )}
    </GenericTaskLikeComponent>
  );
};

const YepstrPlus = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.silver};
  padding: 1rem 1.5rem;
`;

const TaskBookedComponent = (props: ComponentProps<TaskBookedPayload>) => {
  const {
    payload,
    payload: {
      image,
      yepAge,
      yepFirstName,
      yepAvatar,
      isCompany,
      subcategories,
      scheduledAt,
      duration,
      scheduleType,
    },
    translate,
    typedTranslate,
  } = props;

  const items = [];
  if (subcategories.length > 0) {
    items.push({
      title: translate('subcategories-title'),
      text: subcategories.join(', '),
    });
  }
  if (scheduleType === TaskScheduleType.SPECIFIC) {
    items.push({
      title: translate('scheduledAt-title'),
      text: capitalize(
        createDisplayDateTimeAndPerform({ scheduledAt, duration })
      ),
    });
  }

  return (
    <GenericTaskLikeComponent
      {...props}
      image={image}
      subtitleIconName={isCompany ? 'IoIosBriefcase' : ''}
      subtitle={typedTranslate('subtitle', payload)}
      title={typedTranslate('title', payload)}
      avatar={yepAvatar}
      name={yepFirstName}
      age={yepAge}
    >
      {items.length > 0 && (
        <>
          <div>
            <LabeledItems items={items} />
            <CtaSection>
              <Button label={'Kom igång'} to={ROUTES.DOWNLOAD} fullWidth />
            </CtaSection>
          </div>
        </>
      )}
    </GenericTaskLikeComponent>
  );
};

const CtaSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.silver};
`;

const TaskCompletedComponent = (
  props: ComponentProps<TaskCompletedPayload>
) => {
  const {
    payload,
    payload: {
      image,
      yepAge,
      customerFirstName,
      yepFirstName,
      yepAvatar,
      rating,
      isCompany,
      duration,
      paymentTotal,
      hasYepstrPlus,
      percentFee,
      feeTotal,
    },
    typedTranslate,
  } = props;

  const durationPaymentTranslateModifier = isCompany
    ? 'duration-payment-isCompany'
    : 'duration-payment';
  const subtitle = typedTranslate('subtitle', { customerFirstName });
  return (
    <GenericTaskLikeComponent
      {...props}
      image={image}
      subtitleIconName={isCompany ? 'IoIosBriefcase' : ''}
      subtitle={subtitle}
      title={payload.categoryName}
      avatar={yepAvatar}
      name={yepFirstName}
      age={yepAge}
      rating={rating}
    >
      <RatingSection>
        <Ratings payload={payload} size={12} />
      </RatingSection>
      <TaskDetals>
        <DurationText>
          {typedTranslate(durationPaymentTranslateModifier, {
            durationStr: durationUtil.stringifyLong(duration),
            paymentTotal,
          })}
        </DurationText>
        {hasYepstrPlus && (
          <>
            {!isCompany && (
              <YepstrPlusText>
                {typedTranslate('yepstr-plus-info-fee', {
                  percent: percentFee * 100,
                  feeTotal,
                })}
              </YepstrPlusText>
            )}
            <YepstrPlusInfo
              typedTranslate={typedTranslate}
              isCompany={isCompany}
            />
          </>
        )}
      </TaskDetals>
      <CtaSection>
        <Button label={'Kom igång'} to={ROUTES.DOWNLOAD} fullWidth />
      </CtaSection>
    </GenericTaskLikeComponent>
  );
};

const RatingSection = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.silver};
`;

const DurationText = styled.p`
  font-size: 1.7rem;
  font-weight: 700;
`;

const YepstrPlusText = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.battleshipGrey};
`;

const COMPONENT_MAP: {
  [key in Type]?: React.ComponentType<ListItemProps<any>>;
} = {
  [Type.TASK_BOOKED]: TaskBookedComponent,
  [Type.TASK_COMPLETED]: TaskCompletedComponent,
  [Type.YEP_SEARCH_STARTED]: YepSearchStartedComponent,
  // [Type.YEP_REACHED_LEVEL]: YepReachedLevelComponent,
  // [Type.YEP_FIRST_TASK]: YepFirstTaskComponent,
};

const Header = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
`;

const TaskDetals = styled.div`
  padding: 1rem 1.5rem;
`;

const Modal = props => {
  const { t } = useTranslation();

  const {
    onClose,
    item,
    item: { payload },
  } = props;
  const type = item.type;
  const Component = COMPONENT_MAP[type];

  if (!item.payload) {
    return null;
  }

  const typedTranslate = createTypedTranslate(type, payload, t);

  if (Component) {
    return (
      <Component
        onClose={onClose}
        payload={payload}
        translate={t}
        typedTranslate={typedTranslate}
      />
    );
  }

  return null;
};

export default Modal;
